/**
 * Social-media redirect helpers.
 *
 * Many social platforms (LinkedIn, Instagram, TikTok, Facebook, X/Twitter,
 * Threads, Snapchat, Pinterest, etc.) block or show broken/warning pages
 * when they detect the Referer header coming from a third-party
 * link-in-bio service.
 *
 * To bypass this we route clicks through `/go.html` — a tiny static page
 * that carries `<meta name="referrer" content="no-referrer">` and
 * immediately does `window.location.replace(url)`, so the destination
 * platform sees a direct visit with no referrer.
 */

/**
 * Domains (and subdomains) of social-media platforms known to block or
 * degrade incoming traffic that carries a third-party Referer header.
 *
 * The list is intentionally broad: it is cheap to route through the
 * redirect page and expensive to debug a broken link on a platform
 * we forgot to include.
 */
const SOCIAL_MEDIA_DOMAINS: string[] = [
  // Meta
  'instagram.com',
  'facebook.com',
  'fb.com',
  'fb.me',
  'threads.net',
  'messenger.com',

  // LinkedIn
  'linkedin.com',
  'lnkd.in',

  // X / Twitter
  'twitter.com',
  'x.com',
  't.co',

  // TikTok
  'tiktok.com',
  'vm.tiktok.com',

  // Snapchat
  'snapchat.com',
  'snap.com',

  // Pinterest
  'pinterest.com',
  'pin.it',

  // YouTube (some in-app browsers have issues)
  'youtube.com',
  'youtu.be',

  // Reddit
  'reddit.com',
  'redd.it',

  // Twitch
  'twitch.tv',

  // Discord
  'discord.com',
  'discord.gg',

  // Telegram
  'telegram.me',
  't.me',

  // WhatsApp (link previews / web)
  'whatsapp.com',
  'wa.me',

  // Tumblr
  'tumblr.com',

  // Mastodon instances (most common)
  'mastodon.social',
  'mastodon.online',

  // Bluesky
  'bsky.app',

  // Kwai
  'kwai.com',
];

/**
 * Returns `true` when the given URL points to a social-media domain that
 * is known to block or degrade third-party referrer traffic.
 */
export function isSocialMediaUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return SOCIAL_MEDIA_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
}

export function getSafeHref(url: string): string {
  // Social media apps are especially sensitive to intermediate redirects.
  // The most compatible behavior in in-app browsers is to open the final URL
  // directly and let the anchor element strip referrer information.
  // PublicBio already sets rel="noreferrer" and referrerPolicy="no-referrer".
  return url;
}
